<template>
  <footer ref="footer" class="shell-footer">
    <!-- AdditionalResourcesBlock.vue teleports a page's resources here, above the logo. -->
    <div id="shell-footer-resources" class="shell-footer-resources"></div>
    <div class="shell-footer-spacer"></div>
    <div class="shell-footer-track">
      <div class="container shell-footer-logo-wrapper">
        <!-- The Ekco logo, as on ek.co's footer. -->
        <svg
          class="shell-footer-logo"
          viewBox="0 0 1372 168"
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M1173.16 108.685C1210.84 108.685 1240.74 131.941 1251.51 167.719H1280.82C1268.85 116.437 1226.39 80.6592 1173.16 80.6592C1118.13 80.6592 1077.46 115.244 1066.1 167.719H1095.4C1105.57 131.345 1134.28 108.685 1173.16 108.685Z"
          />
          <path
            d="M1173.16 28.7805C1264.07 28.7805 1329.86 84.8326 1342.42 167.718H1371.13C1357.97 69.3288 1278.42 0.158203 1173.16 0.158203C1070.28 0.158203 990.136 69.9251 975.781 167.718H1004.49C1018.25 85.4288 1084.64 28.7805 1173.16 28.7805Z"
          />
          <path
            d="M340.594 37.7258H389.639V167.719H429.712L523.016 37.7258H579.238L485.934 167.719H521.222L634.862 9.10352H508.064L418.348 134.922V9.10352H311.885V167.719H340.594V37.7258Z"
          />
          <path
            d="M829.247 28.7805C872.31 28.7805 903.412 43.688 921.355 55.6139L899.823 96.7585C883.076 87.8141 862.142 80.6585 829.845 80.6585C777.211 80.6585 735.344 116.436 723.382 167.718H752.689C762.259 135.518 788.575 108.685 829.845 108.685C863.339 108.685 881.282 117.629 898.029 127.766L911.187 135.518L957.839 47.2658L948.868 40.1102C930.326 25.2027 890.253 0.158203 829.845 0.158203C723.382 0.158203 646.226 67.5399 633.666 167.718H662.375C674.337 84.8326 739.531 28.7805 829.247 28.7805Z"
          />
          <path
            d="M239.514 101.53V9.10352H0.271484V167.719H28.9806V37.7258H211.403V72.9074H78.0253V167.719H225.757V139.097H106.734V101.53H239.514Z"
          />
        </svg>
      </div>
    </div>
  </footer>
</template>
<script setup lang="ts">
/**
 * The footer keeps 3rem between the page and the logo. When that 3rem is the only reason the
 * page scrolls, and the content is at least half of it (1.5rem) clear of the logo, the footer
 * is compacted to 1.5rem instead so no scrollbar shows.
 */
const footer = useTemplateRef<HTMLElement>('footer');

const COMPACT_REM = 1.5;

let frame = 0;
const update = () => {
  const el = footer.value;
  const main = el?.parentElement;
  if (!el || !main) return;
  // Measure with the full padding, then decide. Nothing paints in between.
  delete el.dataset.compact;
  const overflow = main.scrollHeight - main.clientHeight;
  const rem = parseFloat(getComputedStyle(document.documentElement).fontSize);
  if (overflow > 0 && overflow <= COMPACT_REM * rem) el.dataset.compact = '';
};
const schedule = () => {
  cancelAnimationFrame(frame);
  frame = requestAnimationFrame(update);
};

let resizeObserver: ResizeObserver | undefined;
let mutationObserver: MutationObserver | undefined;
const observeChildren = (main: HTMLElement) => {
  resizeObserver?.disconnect();
  resizeObserver?.observe(main);
  for (const child of main.children) if (child !== footer.value) resizeObserver?.observe(child);
};

onMounted(() => {
  const main = footer.value?.parentElement;
  if (!main) return;
  resizeObserver = new ResizeObserver(schedule);
  mutationObserver = new MutationObserver(() => {
    observeChildren(main);
    schedule();
  });
  mutationObserver.observe(main, {childList: true});
  observeChildren(main);
  schedule();
});
onBeforeUnmount(() => {
  cancelAnimationFrame(frame);
  resizeObserver?.disconnect();
  mutationObserver?.disconnect();
});
</script>
<style lang="scss" scoped>
$logo-height: 6rem;
$logo-margin-top: 1rem;
$logo-margin-bottom: 0.25rem;

.shell-footer {
  // Behind the page content (the main element isolates its stacking context), so the logo can
  // tuck underneath the content instead of forcing the page to scroll to reveal it.
  position: relative;
  z-index: -1;
  margin-block: auto calc(-1 * var(--shell-main-padding-top, 0px));
  padding-block-start: 3rem; // Least space between the page and the footer
  background: transparent; // Same as the root background

  &[data-compact] {
    padding-block-start: 1.5rem;
  }

  @media print {
    display: none;
  }
}

.shell-footer-resources {
  position: relative; // Paint above the logo
}

.shell-footer-spacer {
  height: calc(#{$logo-height} + #{$logo-margin-top} + #{$logo-margin-bottom});
}

// Extends upwards over the content above. The logo is sticky to the bottom of the scroll area, so it
// stays tucked behind that content until the user scrolls far enough to reach its natural position.
.shell-footer-track {
  position: absolute;
  inset: calc(-1 * #{$logo-height}) 0 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end; // A sticky box only moves up, so rest the logo at the bottom
  pointer-events: none;
}

.shell-footer-logo-wrapper {
  position: sticky;
  bottom: 0;
  display: flow-root;
}

.shell-footer-logo {
  display: block;
  max-width: 100%;
  height: $logo-height;
  margin-inline: auto;
  margin-block: $logo-margin-top $logo-margin-bottom; // Floats a bit above the bottom edge
  color: #ffffff;
}

[data-bs-theme='dark'] .shell-footer-logo {
  color: #3a3a3a;
}
</style>
